import CustomTooltip from '@/components/custom-tooltip';
import {
    MenubarItem,
    MenubarLabel,
    MenubarSeparator,
} from '@/components/ui/menubar';
import { cn } from '@/lib/utils';
import { SortValue } from '@/types/enum';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { Dispatch, memo, SetStateAction } from 'react';
import { SortList, TableQueryParams } from '../table-ag-types';

function createCurrentSortLabel(
    value: string,
    sortList: SortList,
    transDef: any
) {
    const [sortKey, sortOrder] = value.split(' ');
    const sortItem = sortList.find((x) => x.value === sortKey);
    if (sortItem) {
        return (
            <p className='line-clamp-1 [&>*]:mr-1'>
                <span>{sortItem.label}</span>
                <span>-</span>
                <span>
                    {(parseInt(sortOrder) as any) === SortValue.ASC
                        ? transDef('System_ASC')
                        : transDef('System_DESC')}
                </span>
            </p>
        );
    }
    return <></>;
}

function createSortList(
    sorts: SortList,
    sortValues: { key: string; value: number }[] | undefined,
    trans: any
) {
    let arr: SortList = [];
    sorts.forEach((x) => {
        arr = arr.concat([
            {
                label: (
                    <CustomTooltip.Root
                        title={trans?.('System_SortASC')}
                        tooltipContentProps={{ side: 'right' }}
                    >
                        <div className='flex w-full min-w-40 items-center justify-between gap-x-1 font-medium'>
                            {x.label}
                            <ArrowUp
                                size={16}
                                strokeWidth={2.5}
                                className={cn('text-blue-600', {
                                    hidden: !sortValues?.some(
                                        (sv) => sv.key === x.value
                                    ),
                                })}
                            />
                        </div>
                    </CustomTooltip.Root>
                ),
                value: `${x.value} ${SortValue.ASC}`,
                sortKey: `${x.value}`,
            },
        ]);

        if (sortValues) {
            for (let sortValue of sortValues) {
                if (sortValue?.key === x.value) {
                    arr = arr.concat([
                        {
                            label: (
                                <CustomTooltip.Root
                                    title={trans?.('System_SortDESC')}
                                    tooltipContentProps={{ side: 'right' }}
                                >
                                    <div className='flex w-full min-w-40 items-center justify-between gap-x-1 font-medium'>
                                        {x.label}
                                        <ArrowDown
                                            size={16}
                                            strokeWidth={2.5}
                                            className={cn('text-blue-600', {
                                                hidden: !sortValues?.some(
                                                    (sv) => sv.key === x.value
                                                ),
                                            })}
                                        />
                                    </div>
                                </CustomTooltip.Root>
                            ),
                            value: `${x.value} ${SortValue.DESC}`,
                            sortKey: `${x.value}`,
                        },
                    ]);
                }
            }
        }
    });
    return arr;
}

function createSortValues(sortValues?: { key: string; value: number }[]) {
    return sortValues?.map((x) => {
        return x ? `${x.key} ${x.value}` : '';
    });
}

type Props = {
    sortValues?: { key: string; value: number }[];
    sortList: SortList;
    setPageInfo?: Dispatch<SetStateAction<TableQueryParams>>;
};

const TableAGSort = ({ sortValues, sortList, setPageInfo }: Props) => {
    const transDef = useTranslations('System');

    const onChangeSort = (val: string) => {
        const sort = val.split(' ');
        setPageInfo?.((state) => {
            let newSort: { key: string; value: number }[] = [
                ...(state.sorts ?? []),
            ];

            if (newSort.length > 0) {
                // remove old sort
                newSort = newSort.filter((x) => x.key !== sort[0]);
            }

            return {
                ...state,
                sorts: [
                    // ...newSort,
                    {
                        key: sort[0],
                        value: parseInt(sort[1]),
                    },
                ],
            };
        });
    };

    const sorts = createSortList(sortList, sortValues, transDef);
    const values = createSortValues(sortValues);
    const firstValue = values?.[0];

    return (
        <>
            <MenubarLabel className='flex items-center gap-x-3'>
                <span className='inline-block min-w-fit'>
                    {transDef('System_Sort')}:
                </span>
                {createCurrentSortLabel(firstValue || '', sortList, transDef)}
            </MenubarLabel>
            <MenubarSeparator />

            {sorts.map((x, idx) => (
                <MenubarItem
                    className={cn('cursor-pointer px-3', {
                        hidden: values?.includes(x.value),
                    })}
                    key={idx}
                    onSelect={(e) => e.preventDefault()}
                    onClick={() => onChangeSort(x.value)}
                >
                    {x.label}
                </MenubarItem>
            ))}
        </>
    );
};

export default memo(TableAGSort);
